import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, take, timer } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import moment from 'moment';
import { LocalStorageService } from '../../../core/services/browser-storage/local-storage.service';
import { LocalStorageKeys } from '../../../core/services/browser-storage/types/local-storage.enum';
import { UserAppSettings } from '../../../core/services/browser-storage/types/user-app-settings.model';
import { SessionData } from '../models/session-data.model';
import {
    AuthenticateApi,
    ChangePasswordResponseDto,
    CheckSessionResponseDto,
    LoginRequestDto,
    LoginResponseDto,
    RefreshTokenResponseDto,
    SetNewPasswordRequestDto,
    UsersApi,
} from '@tradgine/data-access/auth';
import { err, ok, Result } from '@hereisyourgitanton/core';
import { OrganizationApi } from '@tradgine/data-access/tradgine-data';
import { parseJwt } from '../utils/parse-jwt';
import { getTokenRemainingTtl, isTokenExpired } from '../utils/token-time';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly authApi = inject(AuthenticateApi);
    private readonly usersApi = inject(UsersApi);
    private readonly organizationApi = inject(OrganizationApi);
    private readonly localStorage = inject(LocalStorageService);

    private readonly tokenRecheckIntervalMs = 30_000;
    private readonly refreshThresholdMinutes = 2;

    private tokenWillExpireAt: moment.Moment | null = null;

    readonly isAuthorized = signal(false);
    readonly isSessionEnded = signal(false);

    constructor() {
        this.restoreFromStorageSync();

        timer(0, this.tokenRecheckIntervalMs)
            .pipe(
                filter(() => !!this.getAccessToken() && !!this.tokenWillExpireAt),
                map(() => {
                    const ttl = getTokenRemainingTtl(this.tokenWillExpireAt);
                    return this.getTokenAction(ttl, this.refreshThresholdMinutes);
                }),
                switchMap((action) => this.resolveTokenAction(action)),
                filter((action) => action === TokenAction.EndSession),
                takeUntilDestroyed()
            )
            .subscribe(() => this.endSession());
    }

    login(dto: LoginRequestDto): Observable<Result<LoginResponseDto, Error>> {
        return this.authApi.login(dto).pipe(
            tap((result) => {
                if (!result.isOk()) return;

                const dto = result.unwrap();
                this.updateSession({
                    accessToken: dto.accessToken,
                    refreshToken: dto.refreshToken,
                    mustChangePassword: dto.changePasswordOnLogin,
                });
                this.isAuthorized.set(true);
            })
        );
    }

    logout(): void {
        this.isAuthorized.set(false);
        this.clearSessionData();
        this.tokenWillExpireAt = null;
    }

    endSession(): void {
        this.logout();
        this.isSessionEnded.set(true);
    }

    setNewPassword(payload: SetNewPasswordRequestDto): Observable<Result<ChangePasswordResponseDto, Error>> {
        return this.usersApi.setNewPassword(payload).pipe(
            tap((result) => {
                if (!result.isOk()) return;

                const dto = result.unwrap();
                this.updateSession({
                    accessToken: dto.accessToken,
                    refreshToken: dto.refreshToken,
                    mustChangePassword: false,
                });
            })
        );
    }

    //TODO Fuck this shit too
    getUserInfo(): Observable<Result<UserInfo, Error>> {
        return this.usersApi.me().pipe(
            switchMap((me_result) => {
                if (me_result.isOk()) {
                    return this.organizationApi.getCurrentOrganization().pipe(
                        map((org_result) => {
                            const me = me_result.unwrap();

                            return ok<UserInfo, Error>({
                                fullName: me.fullName,
                                email: me.email,
                                phoneNumber: me.phoneNumber,
                                companyName: org_result.map((org) => org.name).unwrapOr(null),
                                companyAddress: org_result.map((org) => org.legalAddress).unwrapOr(null),
                            });
                        })
                    );
                }

                return of(err<UserInfo, Error>(new Error('Can not get user info')));
            })
        );
    }

    getAccessToken(): string | null {
        return this.getSession()?.accessToken ?? null;
    }

    shouldChangePassword(): boolean {
        return !!this.getSession()?.mustChangePassword;
    }

    getUser(): UserAppSettings | null {
        return this.localStorage.getItem<UserAppSettings>(LocalStorageKeys.UserAppSettings);
    }

    private restoreFromStorageSync(): void {
        const { accessToken, refreshToken } = this.getSession() || {};

        if (!accessToken && !refreshToken) {
            this.isAuthorized.set(false);
            return;
        }

        if (!accessToken && refreshToken) {
            this.restoreSessionWithRefresh();
            return;
        }

        this.setTokenExpiredTime(accessToken);
        this.isAuthorized.set(!isTokenExpired(this.tokenWillExpireAt));
    }

    private restoreSessionWithRefresh(): void {
        this.refreshToken().pipe(take(1)).subscribe((result) => {
            if (result.isOk()) {
                this.isAuthorized.set(true);
            } else {
                this.logout();
            }
        });
    }

    private checkSession(): Observable<Result<CheckSessionResponseDto, Error>> {
        return this.authApi.checkSession();
    }

    private refreshToken(): Observable<Result<RefreshTokenResponseDto, Error>> {
        const refreshToken = this.getSession()?.refreshToken;
        if (!refreshToken) return of(err<RefreshTokenResponseDto, Error>(new Error('No refresh token found')));

        return this.authApi.refresh({ refreshToken }).pipe(
            tap((result) => {
                if (!result.isOk()) return;

                const dto = result.unwrap();
                this.updateSession({
                    accessToken: dto.accessToken,
                    refreshToken: dto.refreshToken,
                });
            })
        );
    }

    private getTokenAction(ttl: number, threshold: number): TokenAction {
        return ttl <= threshold ? TokenAction.RefreshToken : TokenAction.CheckSession;
    }

    private resolveTokenAction(action: TokenAction): Observable<TokenAction> {
        if (action === TokenAction.CheckSession) {
            return this.checkSession().pipe(
                map(() => TokenAction.CheckSession),
            );
        }

        if (action === TokenAction.RefreshToken) {
            return this.refreshToken().pipe(
                map(() => TokenAction.RefreshToken),
            );
        }

        return of(TokenAction.EndSession);
    }

    private getSession(): SessionData | null {
        return this.localStorage.getItem<SessionData>(LocalStorageKeys.SessionData);
    }

    private updateSession(model: Partial<SessionData>): void {
        this.saveSessionData(model);
        this.setTokenExpiredTime(model.accessToken);
    }

    private saveSessionData(patch: Partial<SessionData>): void {
        if (this.localStorage.existsItem(LocalStorageKeys.SessionData)) {
            this.localStorage.updateItem(LocalStorageKeys.SessionData, patch);
        } else {
            this.localStorage.setItem(LocalStorageKeys.SessionData, patch);
        }
    }

    private clearSessionData(): void {
        this.localStorage.removeItem(LocalStorageKeys.SessionData, 'accessToken');
        this.localStorage.removeItem(LocalStorageKeys.SessionData, 'refreshToken');
        this.localStorage.removeItem(LocalStorageKeys.SessionData, 'mustChangePassword');
    }

    private setTokenExpiredTime(token: string): void {
        const parsed = parseJwt(token);
        const exp = parsed['exp'];

        if (typeof exp === 'number') {
            this.tokenWillExpireAt = moment.unix(exp);
        }

        this.updateUserAppSettings(parsed);
    }

    private updateUserAppSettings(payload: Record<string, unknown>): void {
        const { 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name': userName, org_name: orgName } = payload;

        if (userName) this.localStorage.updateItem(LocalStorageKeys.UserAppSettings, { userName });
        if (orgName) this.localStorage.updateItem(LocalStorageKeys.UserAppSettings, { orgName });
    }
}

enum TokenAction {
    CheckSession = 'CheckSession',
    RefreshToken = 'RefreshToken',
    EndSession = 'EndSession',
}

//TODO Fuck this shit
export interface UserInfo {
    fullName: string | null;
    email: string | null;
    phoneNumber: string | null;
    companyName: string | null;
    companyAddress: string | null;
}
